import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Trash2 } from 'lucide-react';
import api from '../utils/api';

/**
 * Super Admin → edit a tenant's admin profile (name, photo, reset password).
 * Props:
 *  - slug: tenant slug
 *  - onSuccess()
 */
export default function TenantAdminModal({ isOpen, onClose, slug, onSuccess }) {
  const [admin, setAdmin] = useState(null);
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [fotoPerfil, setFotoPerfil] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    if (!isOpen || !slug) return;
    setError(''); setSuccess(''); setNovaSenha('');
    setFetching(true);
    api.get(`/superadmin/tenants/${slug}/admin`)
      .then(res => {
        setAdmin(res.data);
        setNomeCompleto(res.data.nomeCompleto || '');
        setFotoPerfil(res.data.fotoPerfil || '');
      })
      .catch(err => setError(err.response?.data?.detail || 'Erro ao carregar admin do tenant'))
      .finally(() => setFetching(false));
  }, [isOpen, slug]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 600_000) { setError('Foto muito grande (máx 600KB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setFotoPerfil(ev.target.result); setError(''); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = { nomeCompleto, fotoPerfil };
      if (novaSenha) {
        if (novaSenha.length < 8) { setError('Senha deve ter ao menos 8 caracteres.'); setLoading(false); return; }
        payload.novaSenha = novaSenha;
      }
      await api.put(`/superadmin/tenants/${slug}/admin`, payload);
      setSuccess('Admin do tenant atualizado.');
      setNovaSenha('');
      onSuccess?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === 'string') {
        setError(d);
      } else if (Array.isArray(d) && d.length > 0) {
        setError(d.map(item => item.msg).join(' — '));
      } else {
        setError('Erro ao salvar.');
      }
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const initial = (nomeCompleto || admin?.username || 'A')[0]?.toUpperCase();

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose} data-testid="tenant-admin-modal-overlay">
      <div className="nexus-modal w-full max-w-lg flex flex-col"
        style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()} data-testid="tenant-admin-modal">
        <div className="h-1.5 rounded-t-[28px] bg-slate-900" />
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Editar Admin do Tenant
            </h2>
            {admin && <p className="text-xs text-slate-400">@{admin.username} — tenant: {slug}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="tenant-admin-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 p-5 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="tenant-admin-modal-error">{error}</div>
          )}
          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">{success}</div>
          )}

          {fetching ? (
            <div className="text-center py-8 text-slate-400 text-sm">Carregando...</div>
          ) : admin && (
            <>
              <div className="flex items-center gap-4">
                {fotoPerfil ? (
                  <img src={fotoPerfil} alt="" className="w-20 h-20 rounded-2xl object-cover border border-slate-200" />
                ) : (
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-slate-900 text-white text-2xl font-bold">
                    {initial}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="fin-btn-secondary text-xs" data-testid="tenant-admin-upload-btn">
                    <Upload size={14} /> {fotoPerfil ? 'Alterar foto' : 'Enviar foto'}
                  </button>
                  {fotoPerfil && (
                    <button type="button" onClick={() => setFotoPerfil('')}
                      className="text-xs text-red-500 hover:underline inline-flex items-center gap-1">
                      <Trash2 size={12} /> Remover
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                  <p className="text-xs text-slate-400">PNG, JPG até 600KB</p>
                </div>
              </div>

              <div>
                <label className="fin-label">Nome completo</label>
                <input type="text" value={nomeCompleto} onChange={e => setNomeCompleto(e.target.value)}
                  className="fin-input" data-testid="tenant-admin-nome-input" />
              </div>

              <div className="border-t border-slate-100 pt-3 mt-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Resetar senha (opcional)</p>
                <input type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)}
                  className="fin-input" placeholder="Deixe em branco para não alterar"
                  data-testid="tenant-admin-senha-input" />
                <p className="text-xs text-slate-400 mt-1">Mínimo 8 caracteres. O admin precisará usar essa nova senha no próximo login.</p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-100 bg-white rounded-b-[28px]">
          <button onClick={onClose} className="fin-btn-secondary" disabled={loading} data-testid="tenant-admin-cancel-btn">Cancelar</button>
          <button onClick={handleSave} disabled={loading || fetching}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
            data-testid="tenant-admin-save-btn">
            {loading ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
